import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  CheckSquare,
  LineChart,
  Settings,
  FileText,
  CalendarClock,
  IdCardIcon,
  Building2,
  CalendarCheck,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import sidebarLogo from "../../src/assets/ALU Logo.png";
import collapsedSidebarLogo from "../../src/assets/ALUzon Logo.png";
import "../styles/admin-base.css";

const defaultNavItems = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, allowedRoles: ["SUPER_ADMIN"] },
  { id: "members", label: "Members", icon: Users, allowedRoles: ["SUPER_ADMIN"] },
  { id: "approvals", label: "Approvals", icon: CheckSquare, allowedRoles: ["SUPER_ADMIN", "APPROVER"] },
  { id: "security", label: "ID Card Management", icon: IdCardIcon, allowedRoles: ["SUPER_ADMIN", "ID_CARD_MANAGER"] },
  { id: "tickets", label: "Tickets", icon: ClipboardList, allowedRoles: ["SUPER_ADMIN", "TICKETS_ADMIN"] },
  { id: "events", label: "Event Management", icon: CalendarClock, allowedRoles: ["SUPER_ADMIN", "EVENT_MANAGER"] },
  { id: "venues", label: "Venue Management", icon: Building2, allowedRoles: ["SUPER_ADMIN", "BOOKING_ADMIN"] },
  { id: "bookings", label: "Client Bookings", icon: CalendarCheck, allowedRoles: ["SUPER_ADMIN", "BOOKING_ADMIN"] },
  { id: "reports", label: "Reports", icon: LineChart, allowedRoles: ["SUPER_ADMIN"] },
  { id: "audit", label: "Audit Log", icon: FileText, allowedRoles: ["SUPER_ADMIN"] },
  { id: "settings", label: "Admin Settings", icon: Settings, allowedRoles: ["SUPER_ADMIN"] },
];

export default function AdminSidebar({ active, onNavigate, items, collapsed, onToggleCollapse }) {
  const [userRoles, setUserRoles] = useState([]);
  const sidebarBrandLogo = collapsed ? collapsedSidebarLogo : sidebarLogo;

  const resolvedItems = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) {
      return defaultNavItems;
    }

    const baseMap = defaultNavItems.reduce((accumulator, entry) => {
      accumulator[entry.id] = entry;
      return accumulator;
    }, {});

    const merged = items
      .map((item) => {
        if (!item || !item.id) {
          return null;
        }
        const base = baseMap[item.id] ?? {};
        const icon = item.icon || base.icon;
        const label = item.label || base.label;
        const allowedRoles = Array.isArray(item.allowedRoles) && item.allowedRoles.length
          ? item.allowedRoles
          : (base.allowedRoles || []);

        if (!label || !icon) {
          return base.label && base.icon
            ? { ...base, allowedRoles: base.allowedRoles || [] }
            : null;
        }

        return {
          ...base,
          ...item,
          icon,
          label,
          allowedRoles,
        };
      })
      .filter(Boolean);

    return merged.length ? merged : defaultNavItems;
  }, [items]);

  useEffect(() => {
    const storedUser = localStorage.getItem("adminUser");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setUserRoles(user.roles || []);
      } catch (e) {
        console.error("Failed to parse admin user", e);
      }
    }
  }, []);

  return ( 
    <aside className={`admin-sidebar ${collapsed ? "is-collapsed" : ""}`}>
      <div className="admin-sidebar__header">
        <button
          type="button"
          className="admin-sidebar__logo-button"
          onClick={() => onNavigate?.("dashboard")}
          aria-label="Go to dashboard"
          title="Go to dashboard"
        >
          <div className={`admin-sidebar__logo ${collapsed ? "admin-sidebar__logo--collapsed" : ""}`}>
            <img src={sidebarBrandLogo} alt="LaborConnect" />
          </div>
        </button>
      </div>

      <button
        type="button"
        className="admin-sidebar__toggle"
        onClick={onToggleCollapse}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
        {!collapsed && <span>Collapse</span>}
      </button>

      <nav className="admin-sidebar__nav">
        {resolvedItems.map((item) => {
          if (item.allowedRoles.length > 0) {
            const normalizedUserRoles = userRoles.map(role => role.toUpperCase());
            const hasAccess = item.allowedRoles.some(role => normalizedUserRoles.includes(role.toUpperCase()));
            if (!hasAccess) return null;
          }

          const Icon = item.icon;
          const isActive = item.id === active;
          return (
            <button
              key={item.id}
              type="button"
              className={`admin-sidebar__item ${isActive ? "active" : ""}`}
              onClick={() => onNavigate?.(item.id)}
              data-tooltip={item.label}
            >
              <Icon size={18} />
              {!collapsed && <span className="admin-sidebar__label">{item.label}</span>}
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

AdminSidebar.propTypes = {
  active: PropTypes.string,
  onNavigate: PropTypes.func,
  collapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    icon: PropTypes.elementType,
    allowedRoles: PropTypes.arrayOf(PropTypes.string),
  })),
};

AdminSidebar.defaultProps = {
  items: defaultNavItems,
  collapsed: false,
  onToggleCollapse: () => {},
};
